import React, { Component } from 'react';
import { ReactComponent as ArrowDown } from '../Icons/arrow-down.svg';
import { ReactComponent as DeleteIcon } from '../Icons/icon-delete.svg';
import './ActionCard.scss';
import TextInputField from '../components/TextInputField';
import ButtonRow from '../credit_cards/ButtonRow';
import ClientsApi from '../api/clients';
import CreditCardsApi from '../api/creditCards';

const initialState = {
  clientId: '',
  clients: [],
  cardholder: '',
  errors: [],
  expirationMonth: '',
  expirationYear: '',
  notes: '',
  querying: false,
  isTokenizationReady: false,
  isSubmitting: false
};

const TOKENIZATION_SOURCE_KEY = process.env.REACT_APP_RUN_PAYMENTS_TOKENIZATION_KEY;
const runEnv = process.env.REACT_APP_RUN_PAYMENTS_ENV; // "sandbox" | "production"

const RUN_PAYMENTS_SCRIPT_URL =
    runEnv === 'production'
    ? 'https://tokenization.runpayments-ab.io/tokenization/v0.3'
    : 'https://tokenization.sandbox.runpayments-ab.io/tokenization/v0.3';

export default class ActionCard extends Component {
  constructor(props) {
    super(props);

    this.closeCard = React.createRef();
    this.cardContainerRef = React.createRef();
    this.state = initialState;
    this.hostedTokenization = null;

    this.handleChange = this.handleChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.displayHeader = this.displayHeader.bind(this);
    this.displayBody = this.displayBody.bind(this);
    this.successHeader = this.successHeader.bind(this);
    this.successBody = this.successBody.bind(this);
  }

  componentDidMount() {
    const { action } = this.props;
    if (action === 'add') {
      this.fetchClients();
      this.loadRunPaymentsScript();
    } else if (action === 'edit') {
      this.updateStateCardInfo(this.props);
    }
  }

  componentDidUpdate(prevProps) {
    const prevCreditCard = prevProps.creditCard;

    if (this.props.action !== prevProps.action) {
      this.updateStateCardInfo(this.props);

      if (this.props.action === 'add' && prevProps.action !== 'add') {
        this.loadRunPaymentsScript();
      }
    } else if (this.props.action === 'edit' && prevCreditCard) {
      const { creditCard } = this.props;
      if (creditCard.id !== prevProps.creditCard.id) {
        this.updateStateCardInfo(this.props);
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    this.cleanupHostedTokenization();
  }

  cleanupHostedTokenization() {
    if (this.hostedTokenization) {
      try {
        if (typeof this.hostedTokenization.destroy === 'function') {
          this.hostedTokenization.destroy();
        }
      } catch (e) {
        console.log('Could not destroy RunPayments instance:', e);
      }
      this.hostedTokenization = null;
    }
  }

  loadRunPaymentsScript() {
    // Check if script is already loaded
    if (window.HostedTokenization) {
      this.initializeRunPayments();
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src*="runpayments"]')) {
      this.waitForRunPayments(() => this.initializeRunPayments());
      return;
    }

    const script = document.createElement('script');
    script.src = RUN_PAYMENTS_SCRIPT_URL;
    script.async = true;

    script.onload = () => {
      console.log('✅ RunPayments script loaded successfully');
      this.initializeRunPayments();
    };

    script.onerror = () => {
      console.error('❌ Failed to load RunPayments script');
      this.setState({
        errors: { general: 'Failed to load payment system. Please refresh the page and try again.' }
      });
    };

    document.head.appendChild(script);
  }

  waitForRunPayments(callback, maxAttempts = 50) {
    let attempts = 0;

    const checkLibrary = () => {
      attempts++;

      if (typeof window.HostedTokenization !== 'undefined') {
        callback();
      } else if (attempts >= maxAttempts) {
        console.error(`❌ RunPayments library failed to load after ${maxAttempts} attempts`);
        this.setState({
          errors: { general: 'Payment system failed to load. Please refresh the page and try again.' }
        });
      } else {
        setTimeout(checkLibrary, 100);
      }
    };

    checkLibrary();
  }

  initializeRunPayments() {
    if (!window.HostedTokenization) {
      console.error('❌ RunPayments library not available');
      this.setState({
        errors: { general: 'Payment system not available. Please refresh the page.' }
      });
      return;
    }

    // Wait for the container to be in the DOM
    const initWhenReady = () => {
      const container = document.getElementById('run-payments-card-container');
      if (!container) {
        // Container not yet mounted, retry
        setTimeout(initWhenReady, 100);
        return;
      }

      try {
        const options = {
          target: '#run-payments-card-container',
          showZip: false,
          requireCvv2: true,
          labelType: 'hidden',
          styles: {
            container:
              'font-family: Roboto, sans-serif; display: flex; align-items: center; gap: 16px; border-bottom: 2px solid #a1a5b3; padding-bottom: 8px;',
            card: 'font-size: 18px; color: #444b68; border: none; outline: none; flex: 2; min-width: 180px;',
            expiryContainer: 'display: flex; align-items: center; gap: 4px;',
            expiryMonth:
              'font-size: 18px; color: #444b68; border: none; outline: none; width: 28px; text-align: center;',
            expirySeparator: 'font-size: 18px; color: #444b68;',
            expiryYear:
              'font-size: 18px; color: #444b68; border: none; outline: none; width: 28px; text-align: center;',
            cvv2: 'font-size: 18px; color: #444b68; border: none; outline: none; width: 45px; text-align: center;'
          }
        };

        this.hostedTokenization = new window.HostedTokenization(TOKENIZATION_SOURCE_KEY, options);

        // Handle when iframe is ready
        this.hostedTokenization.on('ready', () => {
          console.log('✅ RunPayments iframe ready');
          this.setState({ isTokenizationReady: true });
        });

        // Listen for input changes to clear errors
        this.hostedTokenization.on('input', event => {
          if (event.error) {
            console.error('Input validation error:', event.error);
          } else {
            // Clear card-related errors when user is typing
            if (this.state.errors.card) {
              this.setState(prevState => {
                const { card, ...restErrors } = prevState.errors;
                return { errors: restErrors };
              });
            }
          }
        });

        // Timeout check
        setTimeout(() => {
          if (!this.state.isTokenizationReady) {
            console.error('❌ Timeout: RunPayments iframe did not load within 10 seconds');
            this.setState({
              errors: { general: 'Payment form failed to load. Please check your connection and refresh the page.' }
            });
          }
        }, 10000);
      } catch (error) {
        console.error('❌ Failed to initialize RunPayments Hosted Tokenization:', error);
        this.setState({
          errors: { general: 'Failed to initialize secure payment form. Please refresh the page.' }
        });
      }
    };

    initWhenReady();
  }

  updateStateCardInfo(props) {
    const { creditCard = {} } = props,
      { cardholderName: cardholder, clientId, expirationMonth, twoDigitYear, id, notes } = creditCard;

    this.setState({
      cardholder: cardholder,
      clientId: clientId,
      expirationMonth: expirationMonth,
      expirationYear: twoDigitYear,
      id: id,
      notes: notes
    });
  }

  fetchClients() {
    let api = new ClientsApi();
    api.index(data => {
      this.setState({
        clients: data.sort((a, b) => {
          return a.name.localeCompare(b.name);
        })
      });
    });
  }

  handleChange(e) {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  }

  handleBlur(e) {
    const { value, name } = e.target;
    if (!value) {
      this.setState({ errors: { ...this.state.errors, [name]: 'This field is required.' } });
    }
  }

  handleClick = async e => {
    e.preventDefault();

    const { value } = e.target;

    switch (value) {
      case 'add':
        this.handleAddCard();
        break;
      case 'cancel':
      case 'close':
        const { onClose } = this.props;
        if (onClose) {
          onClose(e);
        }
        break;
      case 'delete':
        this.deleteCard();
        break;
      case 'edit':
        this.editCard();
        break;
      default:
        console.error(`value '${value}' is not supported!`);
    }
  };

  handleAddCard = async () => {
    const { clientId, cardholder } = this.state;

    // Validate required fields
    if (!clientId || !cardholder) {
      this.setState({
        errors: {
          ...(!clientId && { clientId: 'This field is required.' }),
          ...(!cardholder && { cardholder: 'This field is required.' })
        }
      });
      return;
    }

    if (!this.state.isTokenizationReady) {
      this.setState({
        errors: { general: 'Payment system is still loading. Please wait a moment and try again.' }
      });
      return;
    }

    // Set loading state
    this.setState({ isSubmitting: true, errors: [] });

    try {
      const result = await this.hostedTokenization.getNonceToken();
      console.log('🎉 Tokenization successful:', result);
      this.addCardToGd2(result);
    } catch (error) {
      console.error('❌ Tokenization failed:', error);

      let errorMessage = 'Payment validation failed. Please check your information and try again.';

      if (error.message) {
        const lowerMessage = error.message.toLowerCase();

        if (lowerMessage.includes('card number') || lowerMessage.includes('invalid card')) {
          errorMessage = 'Please enter a valid card number.';
        } else if (
          lowerMessage.includes('cvv') ||
          lowerMessage.includes('security code') ||
          lowerMessage.includes('required')
        ) {
          errorMessage = 'Please enter a valid CVV/security code.';
        } else if (lowerMessage.includes('expir')) {
          errorMessage = 'Please enter a valid expiry date.';
        } else if (lowerMessage.includes('network') || lowerMessage.includes('connection')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }
      }

      this.setState({ errors: { card: errorMessage }, isSubmitting: false });
    }
  };

  editCard = async () => {
    const { action, creditCard, onSuccess } = this.props,
      { cardholder, expirationMonth, expirationYear, notes } = this.state;

    const formData = {
      id: creditCard.id,
      cardholder: cardholder,
      expirationMonth: expirationMonth,
      expirationYear: `20${expirationYear}`,
      notes: notes
    };

    let api = new CreditCardsApi();
    api.edit(
      formData,
      () => {
        onSuccess({ action: action, success: true });
        this.resetInputs();
      },
      errors => this.setState({ errors: errors })
    );
  };

  deleteCard = async () => {
    const { action, creditCard, onSuccess } = this.props;

    let api = new CreditCardsApi();
    api.delete(
      creditCard,
      () => {
        onSuccess({ action: action, success: true });
        this.resetInputs();
      },
      errors => this.setState({ errors: errors })
    );
  };

  resetInputs = () => {
    this.interval = setInterval(() => this.closeCard.current.click(), 3000);

    this.setState({
      clientId: '',
      cardholder: '',
      errors: [],
      expirationMonth: '',
      expirationYear: '',
      notes: '',
      isSubmitting: false
    });

    // Reset the card form if it exists
    if (this.hostedTokenization) {
      try {
        this.hostedTokenization.resetForm();
      } catch (e) {
        console.log('Could not reset form:', e);
      }
    }
  };

  addCardToGd2 = async tokenResult => {
    const { action, onSuccess } = this.props;
    const { clientId, cardholder, notes } = this.state;

    // Structure data to match internal Rails application format
    const creditCardData = {
      clientId: clientId,
      cardholder: cardholder,
      notes: notes,
      nonce: tokenResult.nonce,
      last4: tokenResult.last4,
      cardType: tokenResult.cardType,
      expirationMonth: tokenResult.expiryMonth,
      expirationYear: tokenResult.expiryYear
    };

    let api = new CreditCardsApi();
    api.create(
      creditCardData,
      data => {
        this.setState({ isSubmitting: false });
        onSuccess({ action: action, success: true });
        this.resetInputs();
      },
      errors => {
        this.setState({ errors: errors, isSubmitting: false });
      }
    );
  };

  successHeader = () => {
    const { action } = this.props;

    if (action === 'add') {
      return 'Success!';
    } else if (action === 'edit') {
      return 'Updated!';
    } else if (action === 'delete') {
      return 'Done!';
    }
  };

  successBody = () => {
    const { action } = this.props;

    if (action === 'add') {
      return 'Your card has been successfully added.';
    } else if (action === 'edit') {
      return 'Your card details have been successfully saved.';
    } else if (action === 'delete') {
      return 'Your card has been successfully deleted.';
    }
  };

  setCardTypeBackground(creditCard) {
    return `label label--small label--${creditCard.cardTypeCode.toLowerCase()}`;
  }

  actionHeaderText = () => {
    const { action, creditCard } = this.props;

    if (action === 'add') {
      return 'Add a Credit Card';
    } else if (action === 'edit') {
      return (
        <React.Fragment>
          Edit <div className={`${this.setCardTypeBackground(creditCard)}`}>{creditCard.cardTypeShortName}</div>
          <span className="u-margin-right-s">• • • •</span>
          {creditCard.lastFour}
        </React.Fragment>
      );
    } else if (action === 'delete') {
      return (
        <React.Fragment>
          Are you sure you want to delete{' '}
          <div className={`${this.setCardTypeBackground(creditCard)}`}>{creditCard.cardTypeShortName}</div>
          <span className="u-margin-right-s">• • • •</span>
          {creditCard.lastFour}?
        </React.Fragment>
      );
    }
  };

  displayHeader = () => {
    if (this.props.success) {
      return <div className="u-center-text">{this.successHeader()}</div>;
    } else {
      return <div>{this.actionHeaderText()}</div>;
    }
  };

  renderClientDetails(action) {
    if (action === 'add') {
      return this.renderSelectContainer();
    }

    return <div className="saved-and-disabled-text">{this.props.creditCard.clientName}</div>;
  }

  renderSelectContainer() {
    let { clients } = this.state,
      clientOptions = client => {
        return (
          <option key={client.id} value={client.id}>
            {client.name}
          </option>
        );
      },
      options = clients.map(clientOptions);

    return (
      <div className="select-container">
        <select
          name="clientId"
          defaultValue=""
          onChange={this.handleChange}
          className={this.state.clientId ? '' : 'placeholder-text'}
          required={true}
        >
          <option disabled hidden value="">
            Select
          </option>
          {options}
        </select>
        <ArrowDown className="select-arrow" />
      </div>
    );
  }

  handleSubmit = e => {
    e.preventDefault();

    const { action } = this.props;

    if (action === 'add') {
      const { clientId, cardholder } = this.state;
      if (!clientId || !cardholder) {
        return false;
      }
    }

    this.handleClick(e);
  };

  renderCreditCardDetails(action) {
    if (action === 'add') {
      return this.renderRunPaymentsCardElement();
    }

    const { creditCard } = this.props,
      { expirationMonth, expirationYear } = this.state;

    return (
      <div className="credit-card-info-lockup">
        <div className="credit-card-type-and-last-four">
          <div className={`${this.setCardTypeBackground(creditCard)}`}>{creditCard.cardTypeShortName}</div>
          <span className="u-margin-right-s">• • • •</span>
          {creditCard.lastFour}
        </div>
        <div className="credit-card-expiration-lockup">
          <input
            id="expirationMonth"
            type="text"
            name="expirationMonth"
            value={expirationMonth}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            autoComplete="off"
            placeholder="MM"
            required={true}
            maxLength={2}
          />
          <span>/</span>
          <input
            id="expirationYear"
            type="text"
            name="expirationYear"
            value={expirationYear}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            autoComplete="off"
            placeholder="YY"
            required={true}
            maxLength={2}
          />
        </div>
      </div>
    );
  }

  renderRunPaymentsCardElement() {
    const { isTokenizationReady } = this.state;

    return (
      <div className="run-payments-container">
        <div
          id="run-payments-card-container"
          ref={this.cardContainerRef}
          style={{
            minHeight: isTokenizationReady ? 'auto' : '50px',
            position: 'relative'
          }}
        >
          {!isTokenizationReady && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '50px',
                color: '#aab7c4',
                fontStyle: 'italic',
                fontSize: '18px'
              }}
            >
              Loading...
            </div>
          )}
        </div>
        {this.state.errors.card && (
          <div className="card-error" style={{ color: '#9e2146', marginTop: '8px', fontSize: '14px' }}>
            {this.state.errors.card}
          </div>
        )}
      </div>
    );
  }

  displayForm() {
    const { action } = this.props,
      { cardholder, clientId, expirationMonth, expirationYear, notes, isTokenizationReady, isSubmitting } = this.state;

    const buttonDisabled =
      !clientId ||
      !cardholder ||
      (action === 'add' && !isTokenizationReady) ||
      (action === 'edit' && (!expirationMonth || !expirationYear)) ||
      isSubmitting;

    return (
      <React.Fragment>
        <div>
          <div className="input-lockup">
            <label>CLIENT</label>
            {this.renderClientDetails(action)}
          </div>
          <TextInputField
            name="cardholder"
            value={cardholder}
            label="CARD HOLDER"
            type="text"
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            required={true}
            showUnderline={true}
            placeholder={'Ford S. Riders'}
            autocomplete="off"
          />
          <div className="input-lockup">
            <label>CREDIT CARD</label>
            {this.renderCreditCardDetails(action)}
          </div>
          <TextInputField
            name="notes"
            label="NOTES (Optional)"
            value={notes}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            placeholder="E.g. Corporate Only"
            multiline={true}
          />
        </div>
        <ButtonRow
          action={action}
          disabled={buttonDisabled}
          onClickCancel={this.handleClick}
          onClickSuccess={e => this.handleClick(e)}
          successText={isSubmitting ? 'Saving...' : 'Save'}
          loading={isSubmitting}
        />
        {this.state.errors.general && <div className="error">{this.state.errors.general}</div>}
        {Object.keys(this.state.errors).length !== 0 && !this.state.errors.general && !this.state.errors.card && (
          <div className="error">Hmm, something went wrong. Please try again.</div>
        )}
      </React.Fragment>
    );
  }

  displayBody = () => {
    if (this.props.success) {
      return <div className="u-center-text">{this.successBody()}</div>;
    }

    const { action } = this.props;

    if (action === 'delete') {
      return (
        <React.Fragment>
          <p>You will not be able to undo this.</p>
          <ButtonRow
            action={action}
            cancelText="Never mind"
            onClickCancel={this.handleClick}
            onClickSuccess={this.handleClick}
            successText="Yes, Delete"
          />
          {this.state.errors.length !== 0 ? (
            <div className="error">Hmm, something went wrong. Please try again.</div>
          ) : null}
        </React.Fragment>
      );
    }

    return this.displayForm();
  };

  render() {
    return (
      <div className="action-card">
        <div
          className="icon-container"
          onClick={e => {
            e.target.value = 'close';
            this.handleClick(e);
          }}
          ref={this.closeCard}
        >
          <DeleteIcon alt="Close" />
        </div>
        <div className="page-header page-header--h2">{this.displayHeader()}</div>
        <div className="body">{this.displayBody()}</div>
      </div>
    );
  }
}
